import rawCatalog from '../../../../../src/jeeum/landingContent.json';

export const landingLanguages = [
  'ko',
  'en',
  'ja',
  'zh',
  'es',
  'pt',
  'de',
  'fr',
  'id',
  'hi',
] as const;

export type LandingLanguage = (typeof landingLanguages)[number];

const localizedGuideLanguages = new Set<LandingLanguage>(['ko', 'en', 'ja']);
const localizedGuideSuffixes = [
  '/use-cases/vocal-mr-practice',
  '/use-cases/guitar-cover-practice',
  '/guides/loop-hard-parts',
] as const;

export interface LandingWorkflowItem {
  readonly number: string;
  readonly title: string;
  readonly description: string;
  readonly outcome: string;
}

export interface LandingUseCase {
  readonly kind: 'vocal' | 'guitar';
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly points: readonly string[];
}

export interface LandingGuide {
  readonly title: string;
  readonly description: string;
  readonly path: string;
  readonly action: string;
}

export interface LandingFaq {
  readonly question: string;
  readonly answer: string;
}

export interface LandingContent {
  readonly locale: LandingLanguage;
  readonly brand: string;
  readonly meta: {
    readonly title: string;
    readonly description: string;
    readonly imageAlt: string;
  };
  readonly nav: {
    readonly workflow: string;
    readonly useCases: string;
    readonly guides: string;
    readonly signIn: string;
    readonly languageLabel: string;
    readonly breadcrumbLabel: string;
  };
  readonly hero: {
    readonly eyebrow: string;
    readonly title: readonly string[];
    readonly description: string;
    readonly primaryAction: string;
    readonly secondaryAction: string;
    readonly imageAlt: string;
    readonly imageCaption: string;
  };
  readonly demoDisclosure: string;
  readonly workflow: readonly LandingWorkflowItem[];
  readonly useCases: readonly LandingUseCase[];
  readonly guides: readonly LandingGuide[];
  readonly guideHubAction: string;
  readonly guideHubPath: string | null;
  readonly faq: readonly LandingFaq[];
  readonly finalCta: {
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly primaryAction: string;
    readonly secondaryAction: string;
  };
  readonly footer: {
    readonly blog: string;
    readonly privacy: string;
    readonly terms: string;
    readonly support: string;
    readonly contact: string;
  };
}

type UnknownRecord = Record<string, unknown>;

const contentKeys = [
  'locale',
  'brand',
  'meta',
  'nav',
  'hero',
  'demoDisclosure',
  'workflow',
  'useCases',
  'guides',
  'guideHubAction',
  'guideHubPath',
  'faq',
  'finalCta',
  'footer',
] as const;
const metaKeys = ['title', 'description', 'imageAlt'] as const;
const navKeys = ['workflow', 'useCases', 'guides', 'signIn', 'languageLabel', 'breadcrumbLabel'] as const;
const heroKeys = [
  'eyebrow',
  'title',
  'description',
  'primaryAction',
  'secondaryAction',
  'imageAlt',
  'imageCaption',
] as const;
const workflowKeys = ['number', 'title', 'description', 'outcome'] as const;
const useCaseKeys = ['kind', 'eyebrow', 'title', 'description', 'points'] as const;
const guideKeys = ['title', 'description', 'path', 'action'] as const;
const faqKeys = ['question', 'answer'] as const;
const finalCtaKeys = [
  'eyebrow',
  'title',
  'description',
  'primaryAction',
  'secondaryAction',
] as const;
const footerKeys = ['blog', 'privacy', 'terms', 'support', 'contact'] as const;

function requireRecord(value: unknown, context: string): UnknownRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${context} must be an object`);
  }
  return value as UnknownRecord;
}

function requireExactKeys(
  record: UnknownRecord,
  keys: readonly string[],
  context: string,
): void {
  const missing = keys.filter((key) => !(key in record));
  if (missing.length > 0) {
    throw new Error(`${context} is missing required keys: ${missing.join(', ')}`);
  }

  const expected = new Set(keys);
  const unexpected = Object.keys(record)
    .filter((key) => !expected.has(key))
    .sort();
  if (unexpected.length > 0) {
    throw new Error(`${context} has unexpected keys: ${unexpected.join(', ')}`);
  }
}

function requireString(record: UnknownRecord, key: string, context: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${context}.${key} must be a non-empty string`);
  }
  return value;
}

function requireStringFields(
  record: UnknownRecord,
  keys: readonly string[],
  context: string,
): void {
  for (const key of keys) {
    requireString(record, key, context);
  }
}

function requireStringArray(
  record: UnknownRecord,
  key: string,
  context: string,
): string[] {
  const value = record[key];
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    value.some((item) => typeof item !== 'string' || item.trim().length === 0)
  ) {
    throw new Error(`${context}.${key} must be a non-empty string array`);
  }
  return value;
}

function requireObjectArray(
  record: UnknownRecord,
  key: string,
  context: string,
): UnknownRecord[] {
  const value = record[key];
  if (!Array.isArray(value)) {
    throw new Error(`${context}.${key} must be an array`);
  }
  return value.map((item, index) => requireRecord(item, `${context}.${key}[${index}]`));
}

function requireExactCount(items: readonly unknown[], count: number, context: string): void {
  if (items.length !== count) {
    throw new Error(`${context} must contain exactly ${count} items; received ${items.length}`);
  }
}

function validateLandingContent(value: unknown, language: LandingLanguage): LandingContent {
  const context = `landingContent.${language}`;
  const content = requireRecord(value, context);
  requireExactKeys(content, contentKeys, context);
  const locale = requireString(content, 'locale', context);
  if (locale !== language) {
    throw new Error(`${context}.locale must be "${language}"; received "${locale}"`);
  }

  const brand = requireString(content, 'brand', context);
  const expectedBrand = language === 'ko' ? '지음' : 'Jeeum';
  if (brand !== expectedBrand) {
    throw new Error(`${context}.brand must be "${expectedBrand}"`);
  }

  const meta = requireRecord(content.meta, `${context}.meta`);
  requireExactKeys(meta, metaKeys, `${context}.meta`);
  requireStringFields(meta, metaKeys, `${context}.meta`);

  const nav = requireRecord(content.nav, `${context}.nav`);
  requireExactKeys(nav, navKeys, `${context}.nav`);
  requireStringFields(nav, navKeys, `${context}.nav`);

  const hero = requireRecord(content.hero, `${context}.hero`);
  requireExactKeys(hero, heroKeys, `${context}.hero`);
  requireStringFields(
    hero,
    [
      'eyebrow',
      'description',
      'primaryAction',
      'secondaryAction',
      'imageAlt',
      'imageCaption',
    ],
    `${context}.hero`,
  );
  requireStringArray(hero, 'title', `${context}.hero`);
  requireString(content, 'demoDisclosure', context);

  const workflow = requireObjectArray(content, 'workflow', context);
  requireExactCount(workflow, 3, `${context}.workflow`);
  workflow.forEach((item, index) => {
    const itemContext = `${context}.workflow[${index}]`;
    requireExactKeys(item, workflowKeys, itemContext);
    requireStringFields(item, workflowKeys, itemContext);
  });

  const useCases = requireObjectArray(content, 'useCases', context);
  requireExactCount(useCases, 2, `${context}.useCases`);
  useCases.forEach((item, index) => {
    const itemContext = `${context}.useCases[${index}]`;
    requireExactKeys(item, useCaseKeys, itemContext);
    requireStringFields(
      item,
      ['kind', 'eyebrow', 'title', 'description'],
      itemContext,
    );
    requireStringArray(item, 'points', itemContext);
  });
  const useCaseKinds = useCases.map((item) => item.kind);
  if (useCaseKinds[0] !== 'vocal' || useCaseKinds[1] !== 'guitar') {
    throw new Error(`${context}.useCases must contain vocal then guitar`);
  }

  const guides = requireObjectArray(content, 'guides', context);
  if (guides.length > 3) {
    throw new Error(`${context}.guides must contain at most 3 items; received ${guides.length}`);
  }
  guides.forEach((item, index) => {
    const itemContext = `${context}.guides[${index}]`;
    requireExactKeys(item, guideKeys, itemContext);
    requireStringFields(item, guideKeys, itemContext);
  });
  requireString(content, 'guideHubAction', context);
  if (typeof content.guideHubPath !== 'string' && content.guideHubPath !== null) {
    throw new Error(`${context}.guideHubPath must be a string or null`);
  }
  if (localizedGuideLanguages.has(language)) {
    const expectedPaths = new Set(
      localizedGuideSuffixes.map((suffix) => `/${language}${suffix}`),
    );
    const guidePaths = guides.map((guide, index) =>
      requireString(guide, 'path', `${context}.guides[${index}]`),
    );
    if (
      guidePaths.some((path) => !expectedPaths.has(path)) ||
      new Set(guidePaths).size !== guidePaths.length
    ) {
      throw new Error(`${context}.guides must use the localized public guide paths`);
    }
    const expectedHubPath = `/${language}/use-cases`;
    if (content.guideHubPath !== expectedHubPath) {
      throw new Error(`${context}.guideHubPath must be "${expectedHubPath}"`);
    }
  } else if (guides.length !== 0 || content.guideHubPath !== null) {
    throw new Error(`${context} must omit guides and use a null guideHubPath`);
  }

  const faq = requireObjectArray(content, 'faq', context);
  requireExactCount(faq, 3, `${context}.faq`);
  faq.forEach((item, index) => {
    const itemContext = `${context}.faq[${index}]`;
    requireExactKeys(item, faqKeys, itemContext);
    requireStringFields(item, faqKeys, itemContext);
  });

  const finalCta = requireRecord(content.finalCta, `${context}.finalCta`);
  requireExactKeys(finalCta, finalCtaKeys, `${context}.finalCta`);
  requireStringFields(finalCta, finalCtaKeys, `${context}.finalCta`);

  const footer = requireRecord(content.footer, `${context}.footer`);
  requireExactKeys(footer, footerKeys, `${context}.footer`);
  requireStringFields(footer, footerKeys, `${context}.footer`);

  return content as unknown as LandingContent;
}

function validateCatalog(value: unknown): Record<LandingLanguage, LandingContent> {
  const catalog = requireRecord(value, 'landingContent catalog');
  const missingLocales = landingLanguages.filter((language) => !(language in catalog));
  if (missingLocales.length > 0) {
    throw new Error(`landingContent catalog is missing locales: ${missingLocales.join(', ')}`);
  }

  const supported = new Set<string>(landingLanguages);
  const unexpectedLocales = Object.keys(catalog).filter((language) => !supported.has(language));
  if (unexpectedLocales.length > 0) {
    throw new Error(
      `landingContent catalog has unexpected locales: ${unexpectedLocales.join(', ')}`,
    );
  }

  const validated = {} as Record<LandingLanguage, LandingContent>;
  for (const language of landingLanguages) {
    validated[language] = validateLandingContent(catalog[language], language);
  }
  return validated;
}

function isLandingLanguage(language: string): language is LandingLanguage {
  return (landingLanguages as readonly string[]).includes(language);
}

const catalog = validateCatalog(rawCatalog);

export function getLandingContent(language: string): LandingContent {
  const content = isLandingLanguage(language) ? catalog[language] : catalog.en;
  return structuredClone(content);
}

export function getDemoPath(language: string): '/demo' | '/en/demo' {
  return language === 'ko' ? '/demo' : '/en/demo';
}
